import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { authFetch } from '../utils/api';

// Static fallback — guarantees UI always renders even if /api/pricing fails
// or the backend schema is stale (pre-bilingual migration).
const getStaticPlans = (t) => [
  {
    id: 'static-basic',
    name_en: 'Basic Plan',
    name_el: 'Basic Plan',
    description_en: t.pricing.premium.description,
    description_el: t.pricing.premium.description,
    monthly_price: 99.99,
    yearly_price: 999.99,
    price_label_en: null,
    price_label_el: null,
    features_en: t.pricing.premium.features,
    features_el: t.pricing.premium.features,
    cta_en: t.pricing.premium.cta,
    cta_el: t.pricing.premium.cta,
    popular_label_en: null,
    popular_label_el: null,
    popular: false,
    active: true,
    order: 1
  },
  {
    id: 'static-professional',
    name_en: 'Professional Plan',
    name_el: 'Professional Plan',
    description_en: t.pricing.ultimate.description,
    description_el: t.pricing.ultimate.description,
    monthly_price: null,
    yearly_price: null,
    price_label_en: t.pricing.ultimate.priceLabel,
    price_label_el: t.pricing.ultimate.priceLabel,
    features_en: t.pricing.ultimate.features,
    features_el: t.pricing.ultimate.features,
    cta_en: t.pricing.ultimate.cta,
    cta_el: t.pricing.ultimate.cta,
    popular_label_en: t.pricing.ultimate.popular,
    popular_label_el: t.pricing.ultimate.popular,
    popular: true,
    active: true,
    order: 2
  }
];

// A plan from the API is considered valid only if it carries the new bilingual
// schema fields. Otherwise we treat the API response as stale and keep the
// static fallback in place — protects the deployed UI from breaking.
const isValidBilingualPlan = (p) =>
  p && typeof p === 'object' && 'name_en' in p && 'features_en' in p;

const PricingSection = () => {
  const [billingInterval, setBillingInterval] = useState('monthly');
  const { t, language } = useLanguage();
  const [plans, setPlans] = useState(() => getStaticPlans(t));

  useEffect(() => {
    let active = true;
    authFetch('/api/pricing')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data) && data.length > 0 && data.every(isValidBilingualPlan)) {
          setPlans(data);
        }
      })
      .catch(() => { /* keep static fallback */ });
    return () => { active = false; };
  }, []);

  // Keep static fallback in sync with language-dependent translations
  useEffect(() => {
    setPlans((prev) => (prev[0]?.id?.startsWith('static-') ? getStaticPlans(t) : prev));
  }, [t]);

  const localized = (plan, field) => plan[`${field}_${language}`] ?? plan[`${field}_en`] ?? '';
  const getPrice = (plan) => billingInterval === 'yearly' ? plan.yearly_price : plan.monthly_price;
  const getInterval = () => billingInterval === 'yearly' ? t.pricing.yearLabel : t.pricing.monthLabel;

  // A plan is "Stripe-enabled" when it has an actual numeric price for the
  // currently-selected billing interval. Professional Plan uses price_label
  // ("Pricing upon agreement") and keeps the existing mailto: behavior.
  const isStripeEnabled = (plan) => {
    const price = billingInterval === 'yearly' ? plan.yearly_price : plan.monthly_price;
    const nameEn = (plan.name_en || '').toLowerCase();
    return price != null && price > 0 && nameEn.includes('basic');
  };

  const [checkoutLoading, setCheckoutLoading] = useState(null); // plan.id while loading

  const handleStripeCheckout = async (plan) => {
    try {
      setCheckoutLoading(plan.id);
      const packageId = billingInterval === 'yearly' ? 'basic_yearly' : 'basic_monthly';
      const res = await authFetch('/api/payments/checkout', {
        method: 'POST',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Checkout failed');
      }
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Stripe checkout error:', e);
      }
      alert(language === 'el'
        ? 'Δεν ήταν δυνατή η έναρξη της πληρωμής. Δοκιμάστε ξανά.'
        : 'Could not start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  return (
    <section id="pricing" className="bg-gradient-to-b from-[#0a0e27] to-[#050811] py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.pricing.title}
          </h2>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            <span className={`text-sm ${billingInterval === 'monthly' ? 'text-white font-semibold' : 'text-white/60'}`}>
              {t.pricing.monthly}
            </span>
            <button
              data-testid="pricing-billing-toggle"
              onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
              className="relative w-14 h-7 bg-white/10 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-400"
              aria-label="Toggle billing interval"
            >
              <div
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${
                  billingInterval === 'yearly' ? 'transform translate-x-7' : ''
                }`}
              />
            </button>
            <span className={`text-sm ${billingInterval === 'yearly' ? 'text-white font-semibold' : 'text-white/60'}`}>
              {t.pricing.yearly}
              <span className="ml-2 inline-block bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="pricing-yearly-savings">
                Save €200
              </span>
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-testid="pricing-plans">
          {plans.map((plan) => {
            const price = getPrice(plan);
            const priceLabel = localized(plan, 'price_label');
            const features = plan[`features_${language}`] ?? plan.features_en ?? [];
            const planName = localized(plan, 'name');
            const popularLabel = localized(plan, 'popular_label');

            return (
              <div
                key={plan.id}
                data-testid={`pricing-plan-${plan.id}`}
                className={`relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border rounded-2xl p-8 transition-all duration-300 ${
                  plan.popular
                    ? 'border-cyan-500/50 shadow-2xl shadow-cyan-500/20 scale-105'
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                {plan.popular && popularLabel && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                      {popularLabel}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{planName}</h3>
                  <div className="mb-4">
                    {price != null ? (
                      <>
                        <span className="text-5xl font-bold text-white">€{price}</span>
                        <span className="text-white/60 text-lg">/{getInterval()}</span>
                      </>
                    ) : (
                      <span className="text-2xl font-semibold text-cyan-400">{priceLabel}</span>
                    )}
                  </div>
                  <p className="text-white/70 text-sm">{localized(plan, 'description')}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start space-x-3">
                      <Check className="text-cyan-400 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  asChild={!isStripeEnabled(plan)}
                  disabled={isStripeEnabled(plan) && checkoutLoading === plan.id}
                  onClick={isStripeEnabled(plan) ? () => handleStripeCheckout(plan) : undefined}
                  className={`w-full ${
                    plan.popular
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white'
                      : 'bg-white text-black hover:bg-white/90'
                  }`}
                  size="lg"
                  data-testid={`pricing-${plan.id}-cta`}
                >
                  {isStripeEnabled(plan) ? (
                    <span>
                      {checkoutLoading === plan.id
                        ? (language === 'el' ? 'Φόρτωση...' : 'Loading...')
                        : (localized(plan, 'cta') || t.pricing.premium.cta)}
                    </span>
                  ) : (
                    <a
                      href={`mailto:contact@heliophant.com?subject=${encodeURIComponent((language === 'el' ? 'Ενδιαφέρομαι για το πακέτο ' : 'Interested in plan ') + planName)}&body=${encodeURIComponent((language === 'el' ? 'Γεια σας,\n\nΕνδιαφέρομαι για το πακέτο ' : 'Hello,\n\nI am interested in the plan ') + planName + (price != null ? ' (€' + price + '/' + getInterval() + ')' : '') + '.\n\n' + (language === 'el' ? 'Παρακαλώ επικοινωνήστε μαζί μου.\n\nΕυχαριστώ' : 'Please contact me.\n\nThank you'))}`}
                    >
                      {localized(plan, 'cta') || t.pricing.premium.cta}
                    </a>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <h3 className="text-2xl font-bold text-white mb-4">
            {t.pricing.secureCheckout}
          </h3>
          <p className="text-white/70 text-sm max-w-2xl mx-auto">
            {t.pricing.secureDesc}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
